import dripCampaignService from '@/services/toolbox/drip-campaign-service';
import apiClient from '../../services/api-client';

const state = {
	all: {
		list: [],
		loading: false,
		error: null,
	},
	current: {
		dripCampaignId: null,
		loading: false,
		error: null,
	},
	leads: {
		onInit: true,
		items: [],
		totalCount: null,
	},
};

const mutations = {
	GET_ALL_DRIP_CAMPAIGNS_SUCCESS(state, payload) {
		state.all = {
			list: [...payload],
			loading: false,
			error: null,
		};
	},
	GET_ALL_DRIP_CAMPAIGNS_LOADING(state, payload) {
		state.all = {
			list: [],
			loading: true,
			error: false,
		};
	},
	GET_ALL_DRIP_CAMPAIGNS_ERROR(state, payload) {
		state.all = {
			list: [],
			loading: false,
			error: payload,
		};
	},
	SET_CURRENT_CAMPAIGN(state, payload) {
		state.current.dripCampaignId = payload;
	},
	RESET_CURRENT_DRIP_CAMPAIGN(state) {
		state.current = {
			...{
				dripCampaignId: null,
				loading: false,
				error: null,
			},
		};
	},
	SET_CURRENT_DRIP_CAMPAIGN_LOADING(state, payload) {
		state.current = { ...state.current, loading: payload };
	},
	GET_CURRENT_DRIP_CAMPAIGN_SUCCESS(state, payload) {
		if (state.all.list.length === 0) {
			state.all = {
				list: [payload],
				loading: false,
				error: null,
			};
		}
	},
	GET_CURRENT_DRIP_CAMPAIGN_ERROR(state, payload) {
		state.current = {
			...state.current,
			error: payload,
			loading: false,
		};
	},
	SET_CAMPAIGN_SETTING(state, payload) {
		const campaign = state.all.list.find(
			(item) => item.dripCampaignId === payload.dripCampaignId,
		);
		if (campaign) {
			campaign.settings = { activeInd: payload.activeInd };
			state.all = { ...state.all };
		}
	},
	RESET_DRIP_CAMPAIGN_LEADS(state) {
		state.leads.onInit = true;
		state.leads.items = [];
		state.leads.totalCount = null;
	},
	GET_DRIP_CAMPAIGN_LEADS_SUCCESS(state, payload) {
		state.leads.items = [...state.leads.items, ...payload.items];
		state.leads.totalCount = payload.totalCount;
		state.leads.onInit = false;
	},
	GET_DRIP_CAMPAIGN_LEADS_BY_SEARCH_SUCCESS(state, payload) {
		state.leads.items = [...payload.items];
		state.leads.totalCount = payload.totalCount;
	},
};

const actions = {
	async getAllDripCampaigns({ commit }, dripRequest) {
		if (dripRequest.showLoading) {
			commit('GET_ALL_DRIP_CAMPAIGNS_LOADING');
		}
		try {
			const response = await dripCampaignService.getAllDripCampaigns(
				dripRequest.contactId,
				dripRequest.entityId,
			);
			if (response && response.data) {
				commit('GET_ALL_DRIP_CAMPAIGNS_SUCCESS', response.data);
			}
		} catch (error) {
			commit('GET_ALL_DRIP_CAMPAIGNS_ERROR', error);
			return Promise.reject(error);
		}
	},
	// Current Drip Campaign
	setCurrentCampaign({ commit }, dripCampaignId) {
		commit('SET_CURRENT_CAMPAIGN', dripCampaignId);
	},
	resetCurrentDripCampaign({ commit }) {
		commit('RESET_CURRENT_DRIP_CAMPAIGN');
	},
	async getCurrentDripCampaignById(
		{ commit },
		dripCampaignId,
		contactId,
		entityId,
	) {
		commit('SET_CURRENT_DRIP_CAMPAIGN_LOADING', true);
		try {
			//  Two API calls are required to get a complete campaign
			const campaignResponse = await dripCampaignService.getDripCampaignById(
				dripCampaignId,
			);
			const settingsResponse = await dripCampaignService.getCampaignSettings({
				dripCampaignId,
				contactId,
				entityId,
			});
			if (
				campaignResponse &&
				campaignResponse.data &&
				settingsResponse &&
				settingsResponse.data
			) {
				campaignResponse.data = {
					...campaignResponse.data,
					settings: settingsResponse.data,
				};
				commit('GET_CURRENT_DRIP_CAMPAIGN_SUCCESS', campaignResponse.data);
			}
			// TODO: HANDLE MISSING DATA ERROR
			commit('SET_CURRENT_DRIP_CAMPAIGN_LOADING', false);
		} catch (error) {
			commit('GET_CURRENT_DRIP_CAMPAIGN_ERROR', error);
		}
	},
	async saveCampaignSetting({ commit }, settingsInfo) {
		try {
			const response = await dripCampaignService.saveCampaignSetting(
				settingsInfo,
			);
			if (response && response.data === true) {
				commit('SET_CAMPAIGN_SETTING', settingsInfo);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	// Contacts/Leads
	resetDripCampaignContactLeads() {
		commit('RESET_DRIP_CAMPAIGN_LEADS');
	},
	async getDripCampaignContactLeads({ commit }, { payload }) {
		try {
			const response = await apiClient().post(
				'/DripCampaigns/SearchContacts',
				payload,
			);
			if (response && response.data) {
				commit('GET_DRIP_CAMPAIGN_LEADS_BY_SEARCH_SUCCESS', response.data);
			} else if (response && response.data) {
				commit('GET_DRIP_CAMPAIGN_LEADS_SUCCESS', response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
};

const getters = {
	currentDripCampaignActive(state, getters, rootState) {
		/* NOTE: The backend always initially returns campaign as active, 
	even if the user doesn't have the required Blink registrations. */
		if (state.all.list.length > 0) {
			const currentId = parseInt(state.current.dripCampaignId, 10);
			const currentDripCampaign =
				state.all.list.find(
					(campaign) => campaign.dripCampaignId === currentId,
				) || null;
			if (currentDripCampaign && currentDripCampaign.settings) {
				return (
					currentDripCampaign.settings.activeInd &&
					rootState.companyProfile.blinkRegistrationInd &&
					rootState.profile.blinkRegistrationInd
				);
			}
		}
		return null;
	},
	currentDripCampaign(state) {
		const currentId = parseInt(state.current.dripCampaignId, 10);
		return (
			state.all.list.find(
				(campaign) => campaign.dripCampaignId === currentId,
			) || null
		);
	},
};

export default {
	state,
	mutations,
	actions,
	getters,
};
